/**
 * LinkedItemsTable — таблица записей, связанных с данной через item_links.
 * Связи создают link_rules + ручное «+ Добавить связь».
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Input, Popover, Space, Spin, Table, Tag, Tooltip, Typography, message } from 'antd';
import { DeleteOutlined, LinkOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { ItemRow } from '@lens/shared';
import { apiRequest } from '../../shared/api/client.ts';
import { spacing } from '../../shared/theme/tokens.ts';
import { JiraIssueLink } from '../../shared/components/JiraIssueLink.tsx';
import { pullItemJiraLinks } from '../jira/api.ts';
import { queryItems } from './api.ts';

const { Text } = Typography;

interface LinkedItemRow {
  id: string;
  linkId: string;
  fieldDefinitionId: string | null;
  fieldName: string;
  linkType: string;
  direction: 'outgoing' | 'incoming';
  title: string;
  itemTypeName?: string | null;
  fields: Record<string, unknown>;
  jiraIssueKey: string | null;
  jiraStatus: string | null;
}

interface LinkedItemsTableProps {
  spaceId: string;
  itemId: string;
  jiraIssueKey?: string;
  /** Наблюдатель (canEdit=false) не видит контролы добавления/удаления связей и Pull. */
  canEdit?: boolean;
  onItemClick?: (itemId: string) => void;
}

interface DedupedLinkRow {
  /** id связанной записи; ключ дедупликации вместе с direction */
  itemId: string;
  /** Все linkId (несколько при дубле); удаление по нажатию 🗑 удалит ВСЕ */
  linkIds: string[];
  direction: 'outgoing' | 'incoming';
  title: string;
  /** Тип записи на другом конце связи («Личная цель», «Карточка сотрудника»…) */
  itemTypeName: string | null;
  fields: Record<string, unknown>;
  jiraIssueKey: string | null;
  jiraStatus: string | null;
  /** Все типы связи между этой парой (агрегированные) */
  linkTypes: string[];
  /** Имена полей-источников правил связи (агрегированные) */
  fieldNames: string[];
}

async function fetchLinkedItems(spaceId: string, itemId: string): Promise<LinkedItemRow[]> {
  const res = await apiRequest<{ items: LinkedItemRow[] }>(`/api/spaces/${spaceId}/items/${itemId}/links`);
  return res.items;
}

async function createItemLink(
  spaceId: string,
  itemId: string,
  body: { targetItemId: string; fieldDefinitionId?: string; linkType?: string },
): Promise<void> {
  await apiRequest(`/api/spaces/${spaceId}/items/${itemId}/links`, {
    method: 'POST',
    body,
  });
}

async function removeItemLink(spaceId: string, itemId: string, linkId: string): Promise<void> {
  await apiRequest(`/api/spaces/${spaceId}/items/${itemId}/links/${linkId}`, {
    method: 'DELETE',
  });
}

function LinkAddPopover({
  spaceId,
  itemId,
  currentTargetIds,
  onAdded,
}: {
  spaceId: string;
  itemId: string;
  currentTargetIds: string[];
  onAdded: () => Promise<void>;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<ItemRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [submittingId, setSubmittingId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const trimmed = search.trim();
    if (!trimmed) {
      setSearchResults([]);
      return;
    }

    const timer = window.setTimeout(() => {
      void (async () => {
        setSearching(true);
        try {
          const result = await queryItems(spaceId, {
            search: trimmed,
            limit: 20,
          });
          setSearchResults(result.items.filter((entry) => !currentTargetIds.includes(entry.id)));
        } catch {
          setSearchResults([]);
        } finally {
          setSearching(false);
        }
      })();
    }, 300);

    return () => window.clearTimeout(timer);
  }, [open, search, spaceId, currentTargetIds]);

  const handleAdd = useCallback(async (targetItemId: string) => {
    setSubmittingId(targetItemId);
    try {
      await createItemLink(spaceId, itemId, { targetItemId });
      void message.success('Связь добавлена');
      setOpen(false);
      setSearch('');
      setSearchResults([]);
      await onAdded();
    } catch (error) {
      void message.error(error instanceof Error ? error.message : 'Не удалось добавить связь');
    } finally {
      setSubmittingId(null);
    }
  }, [spaceId, itemId, onAdded]);

  const renderSearchRows = () => {
    if (searching) {
      return (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin size="small" />
        </div>
      );
    }
    if (search.trim().length === 0) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Введите запрос для поиска" />;
    }
    if (searchResults.length === 0) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ничего не найдено" />;
    }
    return searchResults.map((item) => {
      const title = (item.fields.title as string) ?? (item.fields.name as string) ?? item.id.slice(0, 8);
      return (
        <div
          key={item.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '6px 0',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <Text style={{ fontSize: 13 }} ellipsis>{title}</Text>
          <Button
            size="small"
            type="link"
            onClick={() => void handleAdd(item.id)}
            loading={submittingId === item.id}
          >
            Добавить
          </Button>
        </div>
      );
    });
  };

  const content = (
    <div style={{ width: 520 }}>
      <Input
        size="small"
        prefix={<SearchOutlined />}
        placeholder="Поиск записей..."
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        autoFocus
        allowClear
      />
      <div style={{ marginTop: 8, maxHeight: 240, overflowY: 'auto' }}>
        {renderSearchRows()}
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      title="Добавить связь"
      trigger="click"
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setSearch('');
          setSearchResults([]);
        }
      }}
      placement="bottomLeft"
    >
      <Button size="small" type="dashed" icon={<PlusOutlined />}>
        Добавить связь
      </Button>
    </Popover>
  );
}

export function LinkedItemsTable({ spaceId, itemId, jiraIssueKey, canEdit = true, onItemClick }: LinkedItemsTableProps) {
  const [loading, setLoading] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [items, setItems] = useState<LinkedItemRow[]>([]);
  const [deletingLinkId, setDeletingLinkId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchLinkedItems(spaceId, itemId);
      setItems(result);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [spaceId, itemId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handlePullFromJira = async () => {
    setPulling(true);
    try {
      const result = await pullItemJiraLinks(spaceId, itemId);
      void message.success(`Связи из Jira: добавлено ${result.linksSynced}, пропущено ${result.skipped}`);
      await load();
    } catch (err: unknown) {
      void message.error(err instanceof Error ? err.message : 'Не удалось подтянуть связи из Jira');
    } finally {
      setPulling(false);
    }
  };

  const handleDelete = useCallback(async (linkId: string) => {
    setDeletingLinkId(linkId);
    try {
      await removeItemLink(spaceId, itemId, linkId);
      void message.success('Связь удалена');
      await load();
    } catch (error) {
      void message.error(error instanceof Error ? error.message : 'Не удалось удалить связь');
    } finally {
      setDeletingLinkId(null);
    }
  }, [spaceId, itemId, load]);

  // Дедупликация: одна запись + одно направление = один ряд, даже если
  // в БД лежит несколько item_links (разные linkType / fieldDefinitionId).
  // Σ вес считается по уникальным таргетам — иначе один и тот же 5%
  // суммируется дважды (см. баг с «Карточка сотрудника» Q2 2026).
  const rows = useMemo<DedupedLinkRow[]>(() => {
    const byKey = new Map<string, DedupedLinkRow>();
    for (const item of items) {
      const key = `${item.direction}:${item.id}`;
      const existing = byKey.get(key);
      if (existing) {
        if (!existing.linkTypes.includes(item.linkType)) {
          existing.linkTypes.push(item.linkType);
        }
        if (item.fieldDefinitionId && item.fieldName && !existing.fieldNames.includes(item.fieldName)) {
          existing.fieldNames.push(item.fieldName);
        }
        existing.linkIds.push(item.linkId);
      } else {
        byKey.set(key, {
          itemId: item.id,
          linkIds: [item.linkId],
          direction: item.direction,
          title: item.title,
          itemTypeName: item.itemTypeName ?? null,
          fields: item.fields,
          jiraIssueKey: item.jiraIssueKey,
          jiraStatus: item.jiraStatus,
          linkTypes: [item.linkType],
          fieldNames: item.fieldDefinitionId && item.fieldName ? [item.fieldName] : [],
        });
      }
    }
    return Array.from(byKey.values());
  }, [items]);

  const weightSum = rows.reduce((sum, row) => {
    const weight = typeof row.fields.weight === 'number' ? row.fields.weight : 0;
    return sum + weight;
  }, 0);

  const relatesTargetIds = useMemo(
    () => rows.filter((row) => row.direction === 'outgoing').map((row) => row.itemId),
    [rows],
  );

  const handleDeleteRow = useCallback(async (row: DedupedLinkRow) => {
    // Дубль — несколько item_links между той же парой; удаляем все по очереди.
    for (const linkId of row.linkIds) {
      await handleDelete(linkId);
    }
  }, [handleDelete]);

  const columns = [
    {
      title: 'Цель',
      key: 'title',
      ellipsis: true,
      render: (_: unknown, record: DedupedLinkRow) => (
        <div style={{ minWidth: 0 }}>
          <Tooltip title={record.title}>
            <a
              onClick={() => onItemClick?.(record.itemId)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                color: 'var(--color-primary)',
                cursor: 'pointer',
                overflow: 'hidden',
              }}
            >
              <LinkOutlined style={{ flex: '0 0 auto' }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                {record.title}
              </span>
            </a>
          </Tooltip>
          {record.fieldNames.length > 0 && (
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <Text type="secondary" style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                через поле {record.fieldNames.join(', ')}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Связь',
      key: 'linkTypes',
      width: 128,
      render: (_: unknown, record: DedupedLinkRow) => (
        <Space size={[2, 2]} wrap>
          {record.linkTypes.map((type) => (
            <Tag key={type} style={{ marginRight: 0 }}>{type}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Тип',
      key: 'itemType',
      width: 140,
      // Тип записи на другом конце связи. Раньше колонка читала поле goal_type
      // из старой схемы, где бизнес/техн./индивид. было значением поля; теперь
      // это отдельные типы записей, и поля goal_type нет — колонка была пустой.
      render: (_: unknown, record: DedupedLinkRow) =>
        record.itemTypeName ? (
          <Text ellipsis={{ tooltip: record.itemTypeName }} style={{ fontSize: 12 }}>
            {record.itemTypeName}
          </Text>
        ) : null,
    },
    {
      title: 'Вес',
      key: 'weight',
      width: 60,
      align: 'center' as const,
      render: (_: unknown, record: DedupedLinkRow) => {
        const weight = record.fields.weight;
        return weight != null ? `${weight}%` : '—';
      },
    },
    {
      title: 'Статус',
      key: 'status',
      width: 92,
      render: (_: unknown, record: DedupedLinkRow) => {
        const status = record.fields.status as string | undefined;
        if (!status) return null;
        const colors: Record<string, string> = {
          new: 'default',
          in_progress: 'processing',
          on_review: 'warning',
          done: 'success',
          cancelled: 'error',
          paused: 'default',
        };
        return <Tag color={colors[status] ?? 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Jira',
      key: 'jira',
      width: 116,
      render: (_: unknown, record: DedupedLinkRow) => {
        if (!record.jiraIssueKey) return <Text type="secondary">—</Text>;
        return (
          <Tooltip title={record.jiraIssueKey}>
            <JiraIssueLink jiraKey={record.jiraIssueKey} compact />
          </Tooltip>
        );
      },
    },
    ...(canEdit ? [{
      key: 'actions',
      width: 56,
      align: 'center' as const,
      render: (_: unknown, record: DedupedLinkRow) => (
        <Tooltip title="Удалить связь">
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            aria-label="Удалить связь"
            onClick={() => void handleDeleteRow(record)}
            loading={record.linkIds.some((id) => deletingLinkId === id)}
          />
        </Tooltip>
      ),
    }] : []),
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: spacing.md,
        }}
      >
        <Text strong style={{ fontSize: 13, color: 'var(--color-text)' }}>
          Связанные записи ({rows.length})
          {weightSum > 0 && (
            <>
              {' — Σ вес: '}
              <Text strong style={{ color: weightSum === 100 ? 'var(--color-success)' : 'var(--color-error)' }}>
                {weightSum}%
              </Text>
            </>
          )}
        </Text>
        <Space size={spacing.sm}>
          {canEdit && (
            <LinkAddPopover
              spaceId={spaceId}
              itemId={itemId}
              currentTargetIds={relatesTargetIds}
              onAdded={load}
            />
          )}
          {canEdit && jiraIssueKey && (
            <Button size="small" onClick={() => void handlePullFromJira()} loading={pulling}>
              Pull from Jira
            </Button>
          )}
          <Button size="small" icon={<ReloadOutlined />} onClick={() => void load()} loading={loading} />
        </Space>
      </div>

      {loading && rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="small" />
        </div>
      ) : rows.length === 0 ? (
        <Empty description="Нет связанных записей" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Table
          dataSource={rows}
          columns={columns}
          rowKey={(row) => `${row.direction}:${row.itemId}`}
          size="small"
          pagination={false}
          // [no-h-scroll] tableLayout=fixed + ellipsis у «Цель» — таблица влезает
          // в ширину вкладки без горизонтального скролла; длинный заголовок
          // обрезается многоточием (полный — в tooltip), а не растягивает грид.
          tableLayout="fixed"
          locale={{ emptyText: 'Нет связанных записей' }}
        />
      )}
    </div>
  );
}
